import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Paper, Typography, Box, CircularProgress,
    useMediaQuery, IconButton, Chip, Divider, Stack
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import PixIcon from "@mui/icons-material/Pix";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import api from "../../services/api";
import { getApiErrorMessage, normalizePlan } from "../../services/apiUtils";
import { useSnackbar } from "../../contexts/SnackbarContext";

const POLL_INTERVAL_MS = 5000;

function formatBRL(value = 0) {
    const numberValue = Number(value || 0);
    if (!Number.isFinite(numberValue) || numberValue <= 0) return null;

    return numberValue.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
    });
}

function formatDate(value) {
    if (!value) return null;

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;

    return date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
}

function normalizePixData(raw = {}) {
    const pixPayment = raw?.pixPayment && typeof raw.pixPayment === "object" ? raw.pixPayment : null;
    const source = pixPayment || raw;

    return {
        paymentId: source?.paymentId || source?.payment_id || raw?.paymentId || raw?.payment_id || null,
        planName: raw?.planName || raw?.plan || raw?.planSelected || null,
        amount: source?.amount || source?.value || source?.valor || raw?.amount || raw?.value || raw?.valor || null,
        dueDate: source?.dueDate || source?.due_date || raw?.dueDate || raw?.due_date || null,
        status: source?.status || source?.paymentStatus || raw?.status || raw?.paymentStatus || null,
        invoiceUrl: source?.invoiceUrl || source?.invoice_url || raw?.invoiceUrl || raw?.invoice_url || null,
        pixPayload: source?.pixPayload || source?.pix_payload || source?.payload || raw?.pixPayload || raw?.pix_payload || raw?.payload || "",
        pixEncodedImage: source?.pixEncodedImage || source?.pix_encoded_image || source?.encodedImage || raw?.pixEncodedImage || raw?.pix_encoded_image || raw?.encodedImage || "",
        pixExpirationDate: source?.pixExpirationDate || source?.pix_expiration_date || source?.expirationDate || raw?.pixExpirationDate || raw?.pix_expiration_date || raw?.expirationDate || null,
    };
}

function getQrCodeSrc(encodedImage = "") {
    if (!encodedImage) return "";
    return String(encodedImage).startsWith("data:")
        ? encodedImage
        : `data:image/png;base64,${encodedImage}`;
}

export default function PixSubscriptionDialog({
    open,
    onClose,
    onPaid,
    pixData,
    planName,
    amount,
    title = "Pagamento via PIX",
    subtitle = "Pague a mensalidade para liberar seu plano.",
    closeLabel = "Fechar",
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const pollRef = useRef(null);
    const { showSnackbar } = useSnackbar();

    const [checking, setChecking] = useState(false);
    const [paid, setPaid] = useState(false);

    const data = useMemo(() => {
        const normalized = normalizePixData(pixData || {});
        return {
            ...normalized,
            planName: normalized.planName || planName,
            amount: normalized.amount || amount,
        };
    }, [pixData, planName, amount]);

    const formattedAmount = formatBRL(data.amount);
    const formattedDueDate = formatDate(data.dueDate);
    const formattedExpirationDate = formatDate(data.pixExpirationDate);
    const qrCodeSrc = getQrCodeSrc(data.pixEncodedImage);

    const stopPolling = () => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    };

    const checkPayment = async ({ silent = false } = {}) => {
        try {
            if (!silent) setChecking(true);
            const res = await api.get("/billing/plan");
            const plan = normalizePlan(res.data);

            if (String(plan?.status || "").toUpperCase() === "A") {
                setPaid(true);
                stopPolling();
                showSnackbar("Pagamento confirmado! Seu plano foi liberado.", "success");
                onPaid?.(plan);
                return true;
            }

            if (!silent) {
                showSnackbar("Ainda não identificamos o pagamento. Assim que confirmar, o plano será liberado automaticamente.", "info");
            }

            return false;
        } catch (err) {
            if (!silent) {
                showSnackbar(getApiErrorMessage(err, "Não foi possível consultar o pagamento agora."), "warning");
            }
            return false;
        } finally {
            if (!silent) setChecking(false);
        }
    };

    useEffect(() => {
        if (!open) {
            stopPolling();
            setChecking(false);
            setPaid(false);
            return;
        }

        stopPolling();
        pollRef.current = setInterval(() => {
            checkPayment({ silent: true });
        }, POLL_INTERVAL_MS);

        return stopPolling;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, data.paymentId]);

    const handleCopy = async () => {
        if (!data.pixPayload) return;

        try {
            await navigator.clipboard.writeText(data.pixPayload);
            showSnackbar("Código PIX copiado.", "info");
        } catch {
            showSnackbar("Não foi possível copiar automaticamente. Selecione e copie o código manualmente.", "warning");
        }
    };

    const handleClose = () => {
        stopPolling();
        onClose?.();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            fullScreen={isMobile}
            PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
        >
            <DialogTitle sx={{ fontWeight: 900, pr: 6 }}>
                {title}
                <IconButton onClick={handleClose} sx={{ position: "absolute", right: 8, top: 8 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ textAlign: "center" }}>
                    <Chip
                        icon={<PixIcon />}
                        label={`PIX mensal${data.planName ? ` • ${String(data.planName).toUpperCase()}` : ""}`}
                        color={paid ? "success" : "default"}
                        sx={{ mb: 2, fontWeight: 900 }}
                    />

                    <Typography sx={{ fontWeight: 900, fontSize: isMobile ? 20 : 24 }}>
                        {formattedAmount || "Mensalidade do plano"}
                    </Typography>

                    <Typography sx={{ color: "text.secondary", mt: 0.5, maxWidth: 460, mx: "auto" }}>
                        {paid ? "Pagamento confirmado. Seu acesso já foi liberado." : subtitle}
                    </Typography>

                    <Paper
                        elevation={0}
                        sx={{
                            mt: 2.5,
                            p: 2,
                            borderRadius: 3,
                            border: `1px solid ${alpha(theme.palette.success.main, 0.28)}`,
                            bgcolor: alpha(theme.palette.success.main, theme.palette.mode === "dark" ? 0.1 : 0.06),
                        }}
                    >
                        {qrCodeSrc ? (
                            <Box
                                component="img"
                                alt="QR Code PIX"
                                src={qrCodeSrc}
                                sx={{
                                    width: 230,
                                    maxWidth: "100%",
                                    borderRadius: 2,
                                    bgcolor: "white",
                                    p: 1,
                                }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    minHeight: 160,
                                    display: "grid",
                                    placeItems: "center",
                                    color: "text.secondary",
                                }}
                            >
                                QR Code indisponível no momento. Use o PIX copia e cola abaixo.
                            </Box>
                        )}

                        <Stack
                            direction={isMobile ? "column" : "row"}
                            spacing={1}
                            justifyContent="center"
                            alignItems="center"
                            sx={{ mt: 1.5 }}
                        >
                            {formattedDueDate && (
                                <Chip
                                    size="small"
                                    icon={<AccessTimeRoundedIcon />}
                                    label={`Vencimento: ${formattedDueDate}`}
                                    sx={{ fontWeight: 800 }}
                                />
                            )}

                            {formattedExpirationDate && (
                                <Chip
                                    size="small"
                                    label={`QRCode válido até ${formattedExpirationDate}`}
                                    sx={{ fontWeight: 800 }}
                                />
                            )}
                        </Stack>
                    </Paper>

                    <Box sx={{ mt: 2, textAlign: "left" }}>
                        <Typography sx={{ fontWeight: 900, mb: 0.75 }}>
                            PIX copia e cola
                        </Typography>

                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1.25,
                                borderRadius: 2,
                                wordBreak: "break-all",
                                fontSize: 12,
                                color: data.pixPayload ? "text.secondary" : "text.disabled",
                                bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.04 : 0.02),
                                maxHeight: 96,
                                overflow: "auto",
                            }}
                        >
                            {data.pixPayload || "Código PIX não disponível."}
                        </Paper>

                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<ContentCopyRoundedIcon />}
                            onClick={handleCopy}
                            disabled={!data.pixPayload}
                            sx={{ mt: 1.2, borderRadius: 3, fontWeight: 900, textTransform: "none" }}
                        >
                            Copiar código PIX
                        </Button>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Paper
                        elevation={0}
                        sx={(theme) => ({
                            p: 1.5,
                            borderRadius: 3,
                            textAlign: "left",
                            border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
                            bgcolor: alpha(theme.palette.info.main, theme.palette.mode === "dark" ? 0.1 : 0.06),
                        })}
                    >
                        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                            {paid ? (
                                <CheckCircleRoundedIcon sx={{ color: "success.main", mt: 0.2 }} />
                            ) : (
                                <InfoRoundedIcon sx={{ color: "info.main", mt: 0.2 }} />
                            )}
                            <Box>
                                <Typography sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                                    Como funciona o PIX mensal?
                                </Typography>
                                <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.45, lineHeight: 1.45 }}>
                                    O plano fica pendente até a confirmação do pagamento. Nos próximos meses, uma nova cobrança PIX será gerada e o acesso continua liberado enquanto a mensalidade estiver em dia.
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, gap: 1, flexDirection: isMobile ? "column-reverse" : "row" }}>
                <Button
                    onClick={handleClose}
                    sx={{ fontWeight: 900, textTransform: "none", width: isMobile ? "100%" : "auto" }}
                >
                    {closeLabel}
                </Button>

                <Button
                    variant="contained"
                    onClick={() => checkPayment()}
                    disabled={checking || paid}
                    sx={{ fontWeight: 900, textTransform: "none", width: isMobile ? "100%" : "auto" }}
                >
                    {checking ? <CircularProgress size={20} sx={{ color: "white" }} /> : paid ? "Pagamento confirmado" : "Já paguei"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
